import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2, 
  Shield, 
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  Award,
  MoreHorizontal,
  ArrowLeft,
  Settings,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import ConfirmationModal from '../common/ConfirmationModal';
import ComplianceService from '../../services/complianceService';

const ComplianceManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [complianceData, setComplianceData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [complianceToDelete, setComplianceToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 10,
    totalItems: 0,
    current: 1,
    pages: 1,
    limit: 10
  });

  // ActionDropdown component for actions - matching SubcontractorManagement style
  const ActionDropdown = ({ compliance, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 180);
    }
    setIsOpen((v) => !v);
  };

  const handleView = () => {
    setIsOpen(false);
    compliance && (compliance.id || compliance._id) && onView(compliance);
  };

  const handleEdit = () => {
    setIsOpen(false);
    compliance && (compliance.id || compliance._id) && onEdit(compliance);
  };

  const handleDelete = () => {
    setIsOpen(false);
    compliance && (compliance.id || compliance._id) && onDelete(compliance);
  };

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
        style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto' }}
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && rect && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            top: openUpward
              ? window.innerHeight - rect.top + 8
              : rect.bottom + 8,
            left: rect.left,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={handleView}
                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Details
              </button>
            </li>

            <li>
              <button
                onClick={handleEdit}
                className="flex w-full items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                onClick={handleDelete}
                className="flex w-full items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
    );
  };

  // Transform backend compliance data to frontend format
  const transformComplianceToDisplay = (compliance) => {
    // Map status from backend to display format
    const statusMap = {
      'approved': 'Compliant',
      'pending': 'Under Review',
      'rejected': 'Non-Compliant',
      'suspended': 'Suspended',
      'draft': 'Under Review',
      'expired': 'Non-Compliant'
    };

    // Map audit status
    const auditStatusMap = {
      'approved': 'Passed',
      'pending': 'In Progress',
      'rejected': 'Failed',
      'under-review': 'In Progress'
    };

    // Get overall score
    const overallScore = compliance.assessment?.overallScore || 0;

    // Get violations count
    const violations = compliance.violations?.length || 0;

    // Get certifications
    const certifications = [];
    if (compliance.certifications) {
      if (compliance.certifications.iso9001?.status === 'certified') {
        certifications.push('ISO 9001');
      }
      if (compliance.certifications.iso14001?.status === 'certified') {
        certifications.push('ISO 14001');
      }
      if (compliance.certifications.ohsas18001?.status === 'certified') {
        certifications.push('OHSAS 18001');
      }
      if (compliance.certifications.iso45001?.status === 'certified') {
        certifications.push('ISO 45001');
      }
      if (compliance.certifications.custom) {
        compliance.certifications.custom.forEach(cert => {
          if (cert.status === 'certified' && cert.name) {
            certifications.push(cert.name);
          }
        });
      }
    }

    return {
      id: compliance.recordId || compliance._id,
      _id: compliance._id,
      subcontractor: compliance.subcontractor?.name || 'N/A',
      contactPerson: compliance.subcontractor?.contactPerson || 'N/A',
      category: compliance.subcontractor?.category || compliance.complianceType || 'Other',
      complianceStatus: statusMap[compliance.status] || compliance.status || 'Under Review',
      overallScore: overallScore,
      safetyCompliance: compliance.assessment?.safetyScore || 0,
      insuranceCompliance: 0, // Not in schema, would need to calculate
      licenseCompliance: 0, // Not in schema, would need to calculate
      certificationCompliance: compliance.assessment?.qualityScore || 0,
      auditStatus: auditStatusMap[compliance.review?.approvalStatus] || compliance.review?.approvalStatus || 'Scheduled',
      lastAuditDate: compliance.audit?.lastAuditDate || compliance.review?.reviewDate || null,
      nextAuditDate: compliance.audit?.nextAuditDate || compliance.review?.nextReviewDate || null,
      violations: violations,
      certifications: certifications,
      insuranceExpiry: null, // Not in schema
      licenseExpiry: null, // Not in schema
      location: compliance.subcontractor?.company || 'N/A',
      complianceData: compliance // Keep full data for reference
    };
  };

  useEffect(() => {
    loadComplianceData();
  }, [pagination.page, searchTerm, statusFilter, categoryFilter]);

  // Debounce search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pagination.page === 1) {
        loadComplianceData();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page || 1,
        limit: pagination.itemsPerPage || 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(categoryFilter && { complianceCategory: categoryFilter })
      };

      const response = await ComplianceService.getCompliances(params);
      
      if (response && response.success) {
        // Transform backend data to display format
        const transformedData = (response.data || []).map(transformComplianceToDisplay);
        setComplianceData(transformedData);
        
        // Update pagination from response
        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.pagination.current || response.pagination.page || 1,
            pages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            limit: response.pagination.limit || 10
          }));
        }
      } else {
        setComplianceData([]);
        if (response && !response.data) {
          showError('Failed to load compliance records.');
        }
      }
    } catch (err) {
      console.error('Error loading compliance data:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to load compliance records.';
      showError(errorMessage);
      setComplianceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadComplianceData();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/compliance-management';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh compliance data',
      disabled: loading
    },
    {
      label: 'Add Compliance Record',
      icon: <Plus size={18} />,
      onClick: () => navigate('/subcontracting/compliance/create'),
      variant: 'primary',
      tooltip: 'Add a new compliance record'
    }
  ];

  const handleView = (compliance) => {
    const complianceId = compliance._id || compliance.id;
    if (complianceId) {
      navigate(`/subcontracting/compliance/view/${complianceId}`);
    } else {
      console.error('Compliance ID not found:', compliance);
      showError('Unable to view compliance record: ID not found');
    }
  };

  const handleEdit = (compliance) => {
    const complianceId = compliance._id || compliance.id;
    if (complianceId) {
      navigate(`/subcontracting/compliance/edit/${complianceId}`);
    } else {
      console.error('Compliance ID not found:', compliance);
      showError('Unable to edit compliance record: ID not found');
    }
  };

  const handleDelete = (compliance) => {
    setComplianceToDelete(compliance);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!complianceToDelete) return;
    
    setIsDeleting(true);
    try {
      const complianceId = complianceToDelete._id || complianceToDelete.id;
      if (!complianceId) {
        throw new Error('Compliance record ID not found');
      }
      
      await ComplianceService.deleteCompliance(complianceId);
      success('Compliance record deleted successfully!');
      await loadComplianceData();
      setShowDeleteModal(false);
      setComplianceToDelete(null);
    } catch (err) {
      console.error('Error deleting compliance record:', err);
      const errorMessage = err?.message || err?.response?.data?.message || 'Failed to delete compliance record.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getPillBadge = (text, variant) => {
    const configMap = {
      compliant:       { color: '#10b981', bg: '#d1fae5' },
      review:          { color: '#f59e0b', bg: '#fef3c7' },
      noncompliant:    { color: '#ef4444', bg: '#fee2e2' },
      suspended:       { color: '#6b7280', bg: '#f3f4f6' },

      passed:          { color: '#10b981', bg: '#d1fae5' },
      inprogress:      { color: '#3b82f6', bg: '#dbeafe' },
      failed:          { color: '#ef4444', bg: '#fee2e2' },
      scheduled:       { color: '#f59e0b', bg: '#fef3c7' }
    };
    
    const config = configMap[variant] || {
      color: '#6b7280',
      bg: '#f3f4f6'
    };

    return (
    <span
    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
    style={{
      backgroundColor: config.bg,
      color: config.color,
      border: `1px solid ${config.color}20`,
      whiteSpace: 'nowrap'
    }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
        {text}
      </span>
    );
  };

  const getScoreBadge = (score) => {
    if (score >= 90) return <span className="badge bg-success">Excellent</span>;
    if (score >= 80) return <span className="badge bg-warning">Good</span>;
    if (score >= 70) return <span className="badge bg-info">Fair</span>;
    return <span className="badge bg-danger">Poor</span>;
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'subcontractor',
      label: 'Subcontractor',
      render: (value, compliance) => (
        <div>
          <div className="fw-bold text-truncate" style={{ maxWidth: '150px' }} title={compliance.subcontractor}>
            {compliance.subcontractor}
          </div>
          <small className="text-muted">{compliance.id}</small>
        </div>
      )
    },
    {
      key: 'complianceStatus',
      label: 'Status',
      render: (_, compliance) => {
        const map = {
          'Compliant': 'compliant',
          'Under Review': 'review',
          'Non-Compliant': 'noncompliant',
          'Suspended': 'suspended'
        };
        return getPillBadge(
          compliance.complianceStatus,
          map[compliance.complianceStatus]
        );
      }
    },
    {
      key: 'overallScore',
      label: 'Compliance Score',
      render: (_, compliance) => (
      <div
      className="flex flex-col items-center justify-center text-center"
      style={{ minWidth: '100px' }}
      >
        <div className="text-[13px] font-bold text-[var(--text-primary)]">
            {compliance.overallScore}%
          </div>

        <div className="mt-1">
          {getScoreBadge(compliance.overallScore)}
        </div>
      </div>
      )
    },
    {
      key: 'auditStatus',
      label: 'Audit Status',
      render: (_, compliance) => {
        const map = {
          'Passed': 'passed',
          'In Progress': 'inprogress',
          'Failed': 'failed',
          'Scheduled': 'scheduled'
        };
        return getPillBadge(
          compliance.auditStatus,
          map[compliance.auditStatus]
        );
      }
    },
    {
      key: 'violations',
      label: 'Violations',
      render: (value, compliance) => (
        <div>
          <div className="fw-bold text-truncate d-block" style={{ maxWidth: '100px' }} title={compliance.violations}>
            {compliance.violations}
          </div>
          <small className="text-muted">Total</small>
        </div>
      )
    },
    {
      key: 'lastAuditDate',
      label: 'Last Audit',
      render: (value, compliance) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '120px' }} title={new Date(compliance.lastAuditDate).toLocaleDateString()}>
            {new Date(compliance.lastAuditDate).toLocaleDateString()}
          </div>
          <small className="text-muted text-truncate d-block" style={{ maxWidth: '120px' }} title={`Next: ${new Date(compliance.nextAuditDate).toLocaleDateString()}`}>
            Next: {new Date(compliance.nextAuditDate).toLocaleDateString()}
          </small>
        </div>
      )
    },
    {
      key: 'certifications',
      label: 'Certifications',
      render: (value, compliance) => (
        <div>
          <div className="fw-medium text-truncate d-block" style={{ maxWidth: '100px' }} title={compliance.certifications.length}>
            {compliance.certifications.length}
          </div>
          <small className="text-muted">Active</small>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, compliance) => {
        if (!compliance || (!compliance.id && !compliance._id)) {
          console.error('Invalid compliance data in render function:', compliance);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown
            compliance={compliance}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Compliance Management"
        description="Ensure subcontractor compliance with regulations and standards"
        actions={headerActions}
      />
      <div className="page-container">
      {/* Compliance Management Table */}
      <DataTable
        data={complianceData}
        columns={columns}
        title="Compliance Management"
        loading={loading}
        pagination={{
          currentPage: pagination.current || pagination.page || 1,
          totalPages: pagination.pages || 1,
          total: pagination.totalItems || 0,
          itemsPerPage: pagination.limit || pagination.itemsPerPage || 10
        }}
        onPageChange={(page) => {
          setPagination(prev => ({
            ...prev,
            page: page,
            current: page
          }));
        }}
        searchPlaceholder="Search compliance records..."
        searchable={true}
        showActions={false}
        emptyMessage="No compliance records found. Click 'Add Compliance Record' to get started."
      />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Compliance Record"
        message={`Are you sure you want to delete compliance record for "${complianceToDelete?.subcontractor}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ComplianceManagement;
